import React, { useState, useEffect, useCallback } from "react";
import { useHotel } from "../contexts/HotelContext";
import Layout from "../components/Layout";
import {
  ServiceAccount,
  ServiceAccountCreateResponse,
  ServiceAccountListResponse,
  createServiceAccount,
  listServiceAccounts,
  deleteServiceAccount,
} from "../services/serviceAccountsApi";

const ServiceAccounts: React.FC = () => {
  const { currentTenant } = useHotel();
  const tenantId = currentTenant?.id;

  const [serviceAccounts, setServiceAccounts] = useState<ServiceAccount[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(5);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  // Create dialog state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState("");

  // Key display dialog state (shown after successful creation)
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [createdAccount, setCreatedAccount] =
    useState<ServiceAccountCreateResponse | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  // Delete confirmation dialog state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAccount, setDeletingAccount] =
    useState<ServiceAccount | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchServiceAccounts = useCallback(async () => {
    if (!tenantId) return;

    try {
      const data: ServiceAccountListResponse = await listServiceAccounts();
      setServiceAccounts(data.items);
      setTotal(data.total);
      setLimit(data.limit);
    } catch (error) {
      console.error("Error fetching service accounts:", error);
      setMessage("Blad ladowania kont serwisowych");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId) {
      fetchServiceAccounts();
    } else {
      setLoading(false);
    }
  }, [tenantId, fetchServiceAccounts]);

  // --- Create flow ---

  const openCreateModal = () => {
    setCreateName("");
    setCreateError("");
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateName("");
    setCreateError("");
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = createName.trim();
    if (trimmedName.length < 3) {
      setCreateError("Nazwa musi miec co najmniej 3 znaki");
      return;
    }
    if (trimmedName.length > 100) {
      setCreateError("Nazwa moze miec maksymalnie 100 znakow");
      return;
    }

    setIsSubmitting(true);
    setCreateError("");

    try {
      const result = await createServiceAccount(trimmedName);
      setCreatedAccount(result);
      setShowCreateModal(false);
      setShowKeyModal(true);
      fetchServiceAccounts();
    } catch (error: any) {
      const serverMessage =
        error.response?.data?.message || error.message || "";
      if (error.response?.status === 409) {
        setCreateError(
          "Osiagnieto limit kont serwisowych (maksymalnie " + limit + ")"
        );
      } else if (error.response?.status === 403) {
        setCreateError("Brak uprawnien do tworzenia kont serwisowych");
      } else {
        setCreateError(serverMessage || "Blad tworzenia konta serwisowego");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Key display (one-time) ---

  const closeKeyModal = () => {
    // SECURITY: Clear the plaintext key from state
    setCreatedAccount(null);
    setShowKeyModal(false);
    setKeyCopied(false);
  };

  const copyToClipboard = async () => {
    if (!createdAccount) return;
    try {
      await navigator.clipboard.writeText(createdAccount.apiKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // --- Delete flow ---

  const openDeleteModal = (account: ServiceAccount) => {
    setDeletingAccount(account);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingAccount(null);
  };

  const handleDelete = async () => {
    if (!deletingAccount) return;

    setIsDeleting(true);

    try {
      await deleteServiceAccount(deletingAccount.id);
      setMessage("Konto serwisowe zostalo usuniete");
      closeDeleteModal();
      fetchServiceAccounts();
      setTimeout(() => setMessage(""), 3000);
    } catch (error: any) {
      const serverMessage =
        error.response?.data?.message || error.message || "";
      if (error.response?.status === 404) {
        setMessage("Konto serwisowe nie zostalo znalezione");
      } else {
        setMessage(serverMessage || "Blad usuwania konta serwisowego");
      }
      closeDeleteModal();
      setTimeout(() => setMessage(""), 3000);
    } finally {
      setIsDeleting(false);
    }
  };

  // --- Formatting ---

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("pl-PL");
  };

  // --- Render ---

  if (loading) {
    return (
      <Layout showBackButton={true}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-neutral-600">Ladowanie...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!tenantId) {
    return (
      <Layout showBackButton={true}>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="text-center">
              <svg
                className="h-12 w-12 text-neutral-400 mx-auto mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m0 0v2m0-2h2m-2 0H10m9-9a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h2 className="text-xl font-semibold text-neutral-900 mb-2">
                Brak dostepu do organizacji
              </h2>
              <p className="text-neutral-600">
                Nie masz dostepu do zadnej organizacji.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showBackButton={true}>
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="p-6">
            <div className="mb-8">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900">
                    Konta serwisowe
                  </h2>
                  <p className="text-sm text-neutral-600 mt-1">
                    Klucze API do integracji system-to-system (platforma, webhook,
                    automatyzacje)
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    Wykorzystano {total} z {limit} dostepnych kont serwisowych
                  </p>
                </div>
                <button
                  onClick={openCreateModal}
                  disabled={total >= limit}
                  data-testid="sa-create-btn"
                  className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Dodaj Service Account
                </button>
              </div>

              {/* Message banner */}
              {message && (
                <div
                  className={`mb-4 p-3 rounded-lg text-sm font-medium ${
                    message.includes("usuniete")
                      ? "bg-success-50 text-success-700 border border-success-200"
                      : "bg-error-50 text-error-700 border border-error-200"
                  }`}
                >
                  {message}
                </div>
              )}

              {/* Table or empty state */}
              {serviceAccounts.length === 0 ? (
                <div className="text-center py-12" data-testid="sa-empty-state">
                  <svg
                    className="mx-auto h-12 w-12 text-neutral-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-semibold text-neutral-900">
                    Brak kont serwisowych
                  </h3>
                  <p className="mt-1 text-sm text-neutral-500">
                    Utworz pierwsze konto serwisowe, aby platforma mogla
                    integrowac sie z systemem.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200" data-testid="sa-table">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Nazwa
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Ostatnie uzycie
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Utworzono
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Akcje
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {serviceAccounts.map((account) => (
                        <tr key={account.id} data-testid={`sa-row-${account.id}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                                <svg
                                  className="h-4 w-4 text-primary-600"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-neutral-900">
                                  {account.name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                account.isActive
                                  ? "bg-success-100 text-success-800"
                                  : "bg-neutral-100 text-neutral-800"
                              }`}
                            >
                              {account.isActive ? "Aktywny" : "Nieaktywny"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                            {formatDate(account.lastUsedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                            {formatDate(account.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => openDeleteModal(account)}
                              data-testid={`sa-delete-btn-${account.id}`}
                              className="text-error-600 hover:text-error-900"
                            >
                              Usun
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Service Account Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="sa-create-modal" role="dialog" aria-modal="true" aria-labelledby="sa-create-modal-title">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 id="sa-create-modal-title" className="text-lg font-semibold text-neutral-900">
                Dodaj Service Account
              </h3>
            </div>
            <form onSubmit={handleCreate} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Nazwa konta serwisowego
                  </label>
                  <input
                    type="text"
                    required
                    minLength={3}
                    maxLength={100}
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    data-testid="sa-name-input"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="np. Platforma - produkcja"
                    autoFocus
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Od 3 do 100 znakow. Nazwa identyfikuje konto w systemie.
                  </p>
                </div>
              </div>

              {createError && (
                <div className="mt-4 p-3 rounded-lg text-sm font-medium bg-error-50 text-error-700 border border-error-200">
                  {createError}
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || createName.trim().length < 3}
                  data-testid="sa-submit-create-btn"
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Tworzenie..." : "Utworz konto"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* API Key Display Modal (one-time) */}
      {showKeyModal && createdAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="sa-key-modal" role="dialog" aria-modal="true" aria-labelledby="sa-key-modal-title">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 id="sa-key-modal-title" className="text-lg font-semibold text-neutral-900">
                Klucz API dla: {createdAccount.name}
              </h3>
            </div>
            <div className="p-6">
              <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 mb-4">
                <div className="flex">
                  <svg
                    className="h-5 w-5 text-warning-400 mr-2 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-warning-800">
                      Uwaga!
                    </h4>
                    <p className="text-sm text-warning-700 mt-1">
                      Skopiuj klucz teraz. Nie bedzie pokazany ponownie.
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative">
                <input
                  type="text"
                  readOnly
                  value={createdAccount.apiKey}
                  data-testid="sa-api-key-value"
                  className="w-full px-3 py-2 pr-24 border border-neutral-300 rounded-lg bg-neutral-50 font-mono text-sm"
                />
                <button
                  onClick={copyToClipboard}
                  data-testid="sa-copy-key-btn"
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded transition-colors"
                >
                  {keyCopied ? "Skopiowano!" : "Kopiuj"}
                </button>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={closeKeyModal}
                  data-testid="sa-close-key-btn"
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                  Rozumiem, skopiowano klucz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="sa-delete-modal" role="dialog" aria-modal="true" aria-labelledby="sa-delete-modal-title">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 id="sa-delete-modal-title" className="text-lg font-semibold text-neutral-900">
                Usun konto serwisowe
              </h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-neutral-700">
                Czy na pewno chcesz usunac konto serwisowe{" "}
                <strong>{deletingAccount.name}</strong>?
              </p>
              <p className="text-sm text-neutral-500 mt-2">
                Klucz API powiazany z tym kontem przestanie dzialac. Tej operacji
                nie mozna cofnac.
              </p>

              {serviceAccounts.length === 1 && (
                <div className="mt-3 p-3 rounded-lg text-sm bg-warning-50 text-warning-700 border border-warning-200">
                  To jest ostatnie konto serwisowe. Po usunieciu nie bedzie
                  zadnych kont serwisowych.
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="px-4 py-2 text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  data-testid="sa-confirm-delete-btn"
                  className="px-4 py-2 bg-error-600 hover:bg-error-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isDeleting ? "Usuwanie..." : "Usun"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ServiceAccounts;
